import { visibleBand } from "@/lib/layout/appArea";

/**
 * Keep the focused field inside the part of the screen the keyboard leaves.
 *
 * iOS scrolls a focused input into view by itself — but only by scrolling the
 * PAGE. Inside a `position: fixed` dialog the page cannot move, so the attempt
 * does nothing and the field stays wherever it was, frequently behind the
 * keyboard. Every popup in this app is such a dialog, which is why this reads
 * as "the field is anywhere but on screen" rather than as a quirk of one form.
 *
 * So we do what the browser wanted to do, against the right box: find the
 * element's own scroll container and move THAT.
 *
 * Installed once from `main.tsx`, next to `installChunkErrorReload`, because it
 * is a property of the platform rather than of any one screen — a per-dialog
 * hook would have to be remembered by every dialog ever added.
 */

/**
 * Editable targets only. Buttons and checkboxes take focus without raising a
 * keyboard, and scrolling to them would move the page under a tap.
 */
const EDITABLE = [
  'input:not([type="checkbox"]):not([type="radio"]):not([type="button"])',
  'input:not([type="submit"]):not([type="reset"]):not([type="hidden"])',
  "textarea",
  '[contenteditable=""]',
  '[contenteditable="true"]',
].join(",");

export type Band = { top: number; bottom: number; height: number };

/**
 * How far the element has to travel to sit inside the band, in pixels:
 * positive means "scroll the content up", negative "down", 0 means it already
 * fits. Pure, so the arithmetic can be tested without a keyboard.
 */
export function scrollDelta(
  rect: { top: number; bottom: number; height: number },
  band: Band,
  margin = 16,
): number {
  const top = band.top + margin;
  const bottom = band.bottom - margin;
  // A field taller than the visible band can never fit: align its top edge,
  // which is where the label and the caret start.
  if (rect.height >= bottom - top) return rect.top - top;
  if (rect.bottom > bottom) return rect.bottom - bottom;
  if (rect.top < top) return rect.top - top;
  return 0;
}

/** The scroll containers between the element and the document, innermost first. */
function scrollableAncestors(el: HTMLElement): HTMLElement[] {
  const out: HTMLElement[] = [];
  for (let n = el.parentElement; n; n = n.parentElement) {
    const style = getComputedStyle(n);
    const scrolls = style.overflowY === "auto" || style.overflowY === "scroll";
    if (scrolls && n.scrollHeight > n.clientHeight + 1) out.push(n);
  }
  return out;
}

export function installKeyboardScrollIntoView(): () => void {
  if (typeof window === "undefined" || !window.visualViewport) return () => {};
  const vv = window.visualViewport;
  let raf = 0;

  const settle = () => {
    const el = document.activeElement as HTMLElement | null;
    if (!el || !el.matches?.(EDITABLE)) return;

    const band = visibleBand();
    // Only while something is actually covering the viewport. Without this the
    // helper would also fire on a desktop where nothing is in the way, and
    // scroll a perfectly visible field for no reason.
    if (band.height >= window.innerHeight - 1) return;

    let delta = scrollDelta(el.getBoundingClientRect(), band);
    if (!delta) return;

    // Spend the delta on the innermost scroller first and pass the remainder
    // outwards: a field inside a scrolling card inside a scrolling dialog needs
    // both to give a little, and the inner one moves the least content.
    for (const scroller of scrollableAncestors(el)) {
      const before = scroller.scrollTop;
      scroller.scrollTop = before + delta;
      delta -= scroller.scrollTop - before;
      if (Math.abs(delta) < 1) return;
    }
  };

  const schedule = () => {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      raf = 0;
      settle();
    });
  };

  /**
   * A focus needs following up on, not just answering once.
   *
   * The keyboard slides in over roughly a third of a second, and the visual
   * viewport reports its way there in steps that stop arriving before it has
   * finished. A single trailing pass therefore measured against a band that was
   * still shrinking and corrected too little — the reported symptom being that
   * the field only came into view once the FIRST KEY was pressed, because
   * typing is what finally produced another event.
   *
   * So the focus is followed for a second, at a tenth of a second apart. Each
   * pass is idempotent: once the field sits inside the band the delta is zero
   * and nothing moves, so the extra passes cost a rect read and no scrolling.
   */
  const FOLLOW_UP_MS = [60, 150, 260, 400, 550, 700, 850, 1000];
  let followUps: ReturnType<typeof setTimeout>[] = [];
  const clearFollowUps = () => {
    followUps.forEach(clearTimeout);
    followUps = [];
  };

  const onFocusIn = () => {
    schedule();
    clearFollowUps();
    followUps = FOLLOW_UP_MS.map((ms) => setTimeout(settle, ms));
  };

  const onFocusOut = () => clearFollowUps();

  document.addEventListener("focusin", onFocusIn);
  document.addEventListener("focusout", onFocusOut);
  vv.addEventListener("resize", schedule);
  vv.addEventListener("scroll", schedule);

  return () => {
    if (raf) cancelAnimationFrame(raf);
    clearFollowUps();
    document.removeEventListener("focusin", onFocusIn);
    document.removeEventListener("focusout", onFocusOut);
    vv.removeEventListener("resize", schedule);
    vv.removeEventListener("scroll", schedule);
  };
}
